/**
 * A `/api/resource` list read with a long `in` filter, sent in batches.
 *
 * `getResource` is a GET, so an `in` filter travels in the URL, and rfqdev refuses long URLs.
 * Measured 2026-11-02 against `Item Price` with real item codes: 100 codes (a 3.6k-character
 * URL) answer 200, 150 codes (5.4k) answer **400**, and 500+ answer 431. An RFQ with a few
 * hundred matched items therefore lost every price, not only the ones past the limit (Akhil:
 * "pricing options are not loaded due high number of items").
 *
 * Batches are cut by the ENCODED SIZE of the list, not a count of codes, because codes vary in
 * length. The budget keeps the whole URL near 2.5k characters, well under where it broke.
 *
 * POST methods such as `items.get_items_by_codes` carry their codes in the body and are not
 * affected: 1,500 codes answered 200 on the same day.
 */

import { getResource, type GatewayQuery, type Paged } from './frappe'

/** Max encoded length of one batch's `["a","b",…]` list. */
export const IN_BATCH_BUDGET = 1800

/** How many batches are in flight at once, so a big RFQ doesn't open dozens of requests together. */
const IN_BATCH_CONCURRENCY = 4

const encodedLength = (value: string) => encodeURIComponent(JSON.stringify(value)).length

/** Splits `values` into consecutive batches whose encoded list stays within the budget. */
export function inBatches(values: readonly string[], budget = IN_BATCH_BUDGET): string[][] {
  const batches: string[][] = []
  let current: string[] = []
  // `[` and `]`, encoded.
  let size = encodeURIComponent('[]').length
  for (const value of values) {
    // The value, plus the encoded comma that separates it from the one before.
    const cost = encodedLength(value) + (current.length ? encodeURIComponent(',').length : 0)
    if (current.length && size + cost > budget) {
      batches.push(current)
      current = []
      size = encodeURIComponent('[]').length
    }
    size += current.length ? cost : encodedLength(value)
    current.push(value)
  }
  if (current.length) batches.push(current)
  return batches
}

type Reader = (doctype: string, q: Omit<GatewayQuery, 'doctype'>) => Promise<Paged<unknown>>

/**
 * `getResource(doctype, q)` with `[field, 'in', values]` added, sent one batch at a time and the
 * rows merged in batch order. `limitFor(n)` sets each batch's page size from its own code count.
 *
 * Any batch failing fails the whole read. Callers already treat a failed price read as "no
 * prices", and a silent partial answer would look like some items simply have none.
 */
export async function getResourceIn<T>(
  doctype: string,
  q: Omit<GatewayQuery, 'doctype'>,
  field: string,
  values: readonly string[],
  read: Reader = getResource,
  limitFor?: (count: number) => number,
): Promise<Paged<T>> {
  const batches = inBatches(values)
  const results: Paged<unknown>[] = new Array(batches.length)
  let next = 0
  const worker = async () => {
    while (next < batches.length) {
      const i = next++
      const batch = batches[i]
      results[i] = await read(doctype, {
        ...q,
        filters: [...(q.filters ?? []), [field, 'in', batch]],
        ...(limitFor ? { limit: limitFor(batch.length) } : {}),
      })
    }
  }
  await Promise.all(Array.from({ length: Math.min(IN_BATCH_CONCURRENCY, batches.length) }, worker))
  const data = results.flatMap((r) => r.data) as T[]
  return { data, total: data.length, limit: data.length, offset: 0 }
}
